// Helper functions for managing custom fonts

// Extract font family name and variant info from filename
export const parseFontFileName = (fileName: string) => {
  // Remove file extension
  const nameWithoutExt = fileName.replace(/\.(otf|ttf|woff|woff2)$/i, '');

  // Remove version prefix if present (e.g., "20.2.1.1-")
  const cleanName = nameWithoutExt.replace(/^\d+(\.\d+)*-/, '');

  // Extended weight/style suffixes for BwNistaGrot family
  const variants = {
    // Basic weights
    Th: { weight: 100, style: 'normal' }, // Thin
    Thlt: { weight: 100, style: 'normal' }, // Thin Light
    Ul: { weight: 200, style: 'normal' }, // Ultra Light
    Lt: { weight: 300, style: 'normal' }, // Light
    Ltlt: { weight: 300, style: 'normal' }, // Light Light
    Rg: { weight: 400, style: 'normal' }, // Regular
    Rglt: { weight: 400, style: 'normal' }, // Regular Light
    Md: { weight: 500, style: 'normal' }, // Medium
    Mdlt: { weight: 500, style: 'normal' }, // Medium Light
    Sb: { weight: 600, style: 'normal' }, // Semi Bold
    Bd: { weight: 700, style: 'normal' }, // Bold
    Bdlt: { weight: 700, style: 'normal' }, // Bold Light
    Eb: { weight: 800, style: 'normal' }, // Extra Bold
    Bl: { weight: 900, style: 'normal' }, // Black
    Blk: { weight: 900, style: 'normal' }, // Black

    // Italic variants
    It: { weight: 400, style: 'italic' }, // Italic
    ThIt: { weight: 100, style: 'italic' }, // Thin Italic
    LtIt: { weight: 300, style: 'italic' }, // Light Italic
    RgIt: { weight: 400, style: 'italic' }, // Regular Italic
    MdIt: { weight: 500, style: 'italic' }, // Medium Italic
    BdIt: { weight: 700, style: 'italic' }, // Bold Italic
    BlIt: { weight: 900, style: 'italic' }, // Black Italic
  };

  // Sort by length (longest first) to match more specific variants first
  const sortedVariants = Object.entries(variants).sort((a, b) => b[0].length - a[0].length);

  // Find variant suffix
  let familyName = cleanName;
  let variant = { weight: 400, style: 'normal' };

  for (const [suffix, variantInfo] of sortedVariants) {
    if (cleanName.endsWith(`-${suffix}`) || cleanName.endsWith(suffix)) {
      // Handle both "-" separator and direct suffix
      familyName = cleanName.replace(new RegExp(`-?${suffix}$`), '');
      variant = variantInfo;
      break;
    }
  }

  return { familyName, variant };
};

export const addCustomFont = (fileName: string, url: string, id: string, mimeType: string) => {
  try {
    const { familyName, variant } = parseFontFileName(fileName);
    const existing = JSON.parse(localStorage.getItem('zngly_custom_fonts') || '{}');

    // Initialize family if it doesn't exist
    if (!existing[familyName]) {
      existing[familyName] = {
        familyName,
        variants: [],
      };
    }

    // Check if this specific variant already exists
    const variantIndex = existing[familyName].variants.findIndex(
      (v: { weight: number; style: string }) => v.weight === variant.weight && v.style === variant.style
    );

    const fontVariant = {
      url,
      id,
      mimeType,
      weight: variant.weight,
      style: variant.style,
      fileName,
    };

    if (variantIndex >= 0) {
      existing[familyName].variants[variantIndex] = fontVariant;
    } else {
      existing[familyName].variants.push(fontVariant);
    }

    localStorage.setItem('zngly_custom_fonts', JSON.stringify(existing));
    return true;
  } catch {
    return false;
  }
};

export const removeCustomFont = (familyName: string, variantId?: string) => {
  try {
    const existing = JSON.parse(localStorage.getItem('zngly_custom_fonts') || '{}');

    if (variantId) {
      // Remove specific variant
      if (existing[familyName]) {
        existing[familyName].variants = existing[familyName].variants.filter((v: { id: string }) => v.id !== variantId);

        // Remove family if no variants left
        if (existing[familyName].variants.length === 0) {
          delete existing[familyName];
        }
      }
    } else {
      // Remove entire family
      delete existing[familyName];
    }

    localStorage.setItem('zngly_custom_fonts', JSON.stringify(existing));
    return true;
  } catch {
    return false;
  }
};

export const getCustomFonts = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('zngly_custom_fonts') || '{}');
    // Convert object to array of family names for backward compatibility
    return Object.keys(stored);
  } catch {
    return [];
  }
};

export const getCustomFontFamilies = () => {
  try {
    return JSON.parse(localStorage.getItem('zngly_custom_fonts') || '{}');
  } catch {
    return {};
  }
};

// Example usage for BwNistaGrot fonts:
// addCustomFont('20.2.1.1-BwNistaGrot-Md.otf', '/wp-content/uploads/2024/01/BwNistaGrot-Md.otf', 'font-1', 'font/otf');
// addCustomFont('20.2.1.1-BwNistaGrot-Bdlt.otf', '/wp-content/uploads/2024/01/BwNistaGrot-Bdlt.otf', 'font-2', 'font/otf');
// addCustomFont('20.2.1.1-BwNistaGrot-Lt.otf', '/wp-content/uploads/2024/01/BwNistaGrot-Lt.otf', 'font-3', 'font/otf');
